import {
  Popover,
  PopoverButton,
  PopoverPanel,
  PopoverPanelProps,
} from "@headlessui/react"
import { Trash } from "lucide-react"
import {
  SortOrder,
  flipDirection,
  getSortDisplayT,
} from "shared-ts/hooks/useSort/helpers"
import { SortProps } from "shared-ts/hooks/useSort/types"

import { SortButton, SortButtonMode } from "./SortButton/SortButton"
import { SortPopoverDirectionToggle } from "./SortPopover/SortPopoverDirectionToggle"
import { SortPopoverButton } from "./SortPopover/SortPopoverDropdownButton"
import {
  modalPopoverClassName,
  popoverClassName,
  popoverPanelAnchor,
} from "../../../../components/base/overlay/Popover/popoverStyles"
import { classNames } from "../../../../lib/classNames"
import { FilteredCombobox } from "../Combobox/FilteredCombobox/FilteredCombobox"
import { IconButton } from "../Filter/FilterHeaderRow"
import { IconSizes } from "../LucideIcon"

interface WebSortProps<T> extends SortProps<T> {
  anchor?: PopoverPanelProps["anchor"]
  isInModal?: boolean
  disabled?: boolean
  buttonMode?: SortButtonMode
}

export function Sort<T>({
  sort,
  items,
  onSelectSortBy,
  keyExtractor,
  valueExtractor,
  accessoryExtractor,
  onSelectDirection,
  anchor = popoverPanelAnchor,
  isInModal,
  disabled,
  buttonMode,
}: WebSortProps<T>) {
  function handleSelectDirection() {
    if (!sort) {
      return onSelectDirection(SortOrder.Ascending)
    }
    onSelectDirection(flipDirection(sort.order))
  }

  const popoverPanelClassName = classNames(
    isInModal ? modalPopoverClassName : popoverClassName,
    "w-fit"
  )

  return (
    <Popover className="relative">
      <PopoverButton
        as={SortButton}
        sort={sort ? getSortDisplayT(sort, valueExtractor, keyExtractor) : null}
        disabled={disabled}
        mode={buttonMode}
      />
      <PopoverPanel anchor={anchor} className={popoverPanelClassName}>
        {({ close }) =>
          !sort ? (
            <FilteredCombobox
              items={items}
              keyExtractor={keyExtractor}
              valueExtractor={valueExtractor}
              accessoryExtractor={accessoryExtractor}
              onSelect={(item) => {
                onSelectSortBy(item)
                close()
              }}
              comboboxProps={{
                comboboxInputProps: {
                  placeholder: "Sort by...",
                },
              }}
            />
          ) : (
            <Popover className="relative">
              <div className="flex w-fit flex-row gap-2 p-2">
                <PopoverButton
                  as={SortPopoverButton}
                  label={
                    getSortDisplayT(sort, valueExtractor, keyExtractor).label
                  }
                />
                <SortPopoverDirectionToggle
                  onClick={handleSelectDirection}
                  sortOrder={sort.order}
                />
                <IconButton
                  onClick={() => {
                    onSelectSortBy(null)
                    close()
                  }}
                  Icon={Trash}
                  wrapperSize={IconSizes.ExtraLarge}
                  iconSize={IconSizes.Small}
                />
              </div>
              <PopoverPanel anchor={anchor} className={popoverPanelClassName}>
                {({ close }) => (
                  <FilteredCombobox
                    items={items}
                    keyExtractor={keyExtractor}
                    valueExtractor={valueExtractor}
                    accessoryExtractor={accessoryExtractor}
                    onSelect={(item) => {
                      onSelectSortBy(item)
                      close()
                    }}
                  />
                )}
              </PopoverPanel>
            </Popover>
          )
        }
      </PopoverPanel>
    </Popover>
  )
}
